import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, XCircle, Download, ScanLine, FileText, AlertTriangle } from "lucide-react";
import api, { API } from "@/lib/apiClient";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STATUS_CLS = {
  ACTIVE: "text-emerald-700 bg-emerald-50 border-emerald-200",
  SUPERSEDED: "text-amber-700 bg-amber-50 border-amber-200",
  WITHDRAWN: "text-red-600 bg-red-50 border-red-200",
  ARCHIVED: "text-slate-500 bg-slate-100 border-slate-200",
};
const fmtDate = (iso) => {
  const d = new Date((iso || "") + "T00:00:00");
  return isNaN(d) ? (iso || "—") : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase();
};

function ResultCard({ d }) {
  const cls = STATUS_CLS[d.status] || STATUS_CLS.ARCHIVED;
  const pdfUrl = `${API}/official-documents/download/${d.doc_id}`;
  return (
    <div data-testid="verify-valid">
      <div className="bg-emerald-50 border border-emerald-200 rounded-md p-6 text-center mb-6">
        <ShieldCheck size={40} className="text-emerald-600 mx-auto mb-2" />
        <h2 className="font-serif text-2xl text-npst-ink">Document Verified</h2>
        <p className="text-sm text-emerald-800 mt-1">✓ Authentic document available in the official records of National Public Service Trust</p>
      </div>

      {d.status === "SUPERSEDED" && d.superseded && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-md p-4 flex items-start gap-3" data-testid="verify-superseded">
          <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <span className="font-semibold">SUPERSEDED.</span> {d.superseded.note}
            {d.superseded.slug && <Link to={`/official-documents/${d.superseded.slug}`} className="block mt-1 font-semibold link-underline" data-testid="verify-view-latest">View Latest Document →</Link>}
          </div>
        </div>
      )}

      <div className="bg-white border border-npst-line rounded-md overflow-hidden">
        <div className={`flex items-center justify-center gap-2 py-3 border-b ${cls}`} data-testid="verify-status">
          <span className="font-semibold uppercase tracking-[0.14em] text-sm">{d.status}</span>
        </div>
        <div className="divide-y divide-npst-line">
          {[["Document Title", d.title], ["Document ID", d.doc_id], ["Category", d.category],
            ["Reference Number", d.reference_no || "—"], ["Document Date", fmtDate(d.doc_date)],
            ["Issuing Authority", d.authority || "—"], ["Number of Pages", d.pages],
            ["Published / Uploaded", fmtDate((d.published_at || "").slice(0, 10))]].map(([k, v]) => (
            <div key={k} className="flex justify-between gap-4 px-6 py-3.5 text-sm">
              <span className="text-npst-muted uppercase tracking-wider text-xs font-semibold shrink-0">{k}</span>
              <span className="text-npst-ink font-medium text-right break-all">{v}</span>
            </div>
          ))}
          {d.sha256 && (
            <div className="px-6 py-3.5">
              <div className="text-npst-muted uppercase tracking-wider text-xs font-semibold mb-1">SHA-256 Document Fingerprint</div>
              <div className="font-mono text-[11px] text-npst-inkSoft break-all" data-testid="verify-sha">{d.sha256}</div>
            </div>
          )}
        </div>
        <div className="p-6 border-t border-npst-line flex flex-wrap gap-3">
          <Link to={`/official-documents/${d.slug}`} className="inline-flex items-center gap-2 px-5 py-3 border border-npst-navy text-npst-navy text-sm font-semibold uppercase tracking-[0.1em] rounded-sm hover:bg-npst-navy hover:text-white transition-colors" data-testid="verify-view-doc"><FileText size={15} /> View Official Document</Link>
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-3 bg-npst-navy text-white text-sm font-semibold uppercase tracking-[0.1em] rounded-sm hover:bg-npst-navyDeep transition-colors" data-testid="verify-download"><Download size={15} /> Download Verified PDF</a>
        </div>
      </div>

      <p className="text-xs text-npst-muted leading-relaxed mt-5 text-center max-w-xl mx-auto">This verification page confirms that the document corresponding to the above Document ID is available in the official digital records of National Public Service Trust. Verify the particulars of the document with the information displayed on this page.</p>
    </div>
  );
}

export default function VerifyDocument() {
  const { docId } = useParams();
  const [data, setData] = useState(undefined);
  const [input, setInput] = useState("");
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (docId) api.get(`/verify/${docId}`).then(({ data }) => setData(data)).catch(() => setData(null));
  }, [docId]);

  const search = async () => {
    if (!input.trim()) return;
    setSearching(true); setData(undefined);
    try {
      const { data } = await api.get(`/verify/${input.trim().toUpperCase()}`);
      setData(data);
    } catch { setData(null); }
    finally { setSearching(false); }
  };

  return (
    <div className="fade-in max-w-2xl mx-auto px-6 py-16">
      <SEO title="Verify an NPST Document — National Public Service Trust" description="Independently verify the authenticity of an official document issued by the National Public Service Trust using its Document ID." />
      <div className="text-center mb-8">
        <div className="label-eyebrow text-npst-gold mb-2">National Public Service Trust</div>
        <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-npst-ink">Verify an NPST Document</h1>
        <p className="text-sm text-npst-muted mt-3">Scan the QR code on the document, or enter its NPST Document ID below.</p>
      </div>

      {!docId && data === undefined && (
        <div className="bg-white border border-npst-line rounded-md p-6" data-testid="verify-search">
          <label className="label-eyebrow text-slate-500 block mb-2">NPST Document ID</label>
          <div className="flex gap-2">
            <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="NPST-XXXXXXXXXX" className="font-mono" data-testid="verify-doc-input" />
            <Button onClick={search} disabled={searching} className="bg-npst-navy hover:bg-npst-navyDeep shrink-0" data-testid="verify-doc-btn"><ScanLine size={15} className="mr-1.5" /> {searching ? "…" : "Verify"}</Button>
          </div>
        </div>
      )}

      {data === undefined && docId ? <p className="text-center text-npst-muted animate-pulse">Verifying…</p>
        : data === null ? (
          <>
            <div className="bg-red-50 border border-red-200 rounded-md p-8 text-center" data-testid="verify-invalid">
              <XCircle size={40} className="text-red-500 mx-auto mb-3" />
              <h2 className="font-serif text-2xl text-npst-ink">Document Not Found</h2>
              <p className="text-sm text-npst-inkSoft mt-2">This Document ID or verification link is invalid, or no such document exists in the official records.</p>
            </div>
            {!docId && <div className="text-center mt-5"><button onClick={() => { setData(undefined); setInput(""); }} className="text-sm text-npst-navy link-underline font-semibold" data-testid="verify-try-again">Try another Document ID</button></div>}
          </>
        ) : data ? <ResultCard d={data} /> : null}
    </div>
  );
}
